import type { JSX } from "react";

import { useNotification } from "@expert/common-ui";
import { Button, Flex, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { useAnalytics } from "@/analytics/hooks/useAnalytics";
import { FTCDisclosureModal } from "@/sales-tools/disclosures/components/FTCDisclosureModal";
import { useLogger, useMostRecentTask, useSessionId } from "@/sdk";

import { type EligibleProduct, useProductOfferStore } from "../../stores";
import { useIntegratedFlowStore } from "../../stores/useIntegratedFlowStore";
import EnrollmentWait from "../Icons/EnrollmentWait.svg";
import classes from "./WaitForEnrollment.module.css";

interface WaitForEnrollmentProps {
    product: EligibleProduct;
}

export function WaitForEnrollment({ product }: WaitForEnrollmentProps): JSX.Element | null {
    const logger = useLogger({ module: "WaitForEnrollment", supportTeam: "sales" });
    const sessionId = useSessionId();
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const { dispatcher } = useAnalytics();
    const notification = useNotification();
    const [opened, { open, close }] = useDisclosure(false);
    const task = useMostRecentTask();
    const language = task?.locale === "es-mx" ? "es-US" : "en-US";
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const { maximumSmartLinksGenerated } = useProductOfferStore
        .getState()
        .getSmartLinkMetaBySessionId(sessionId, selectedProduct?.id ?? primaryProducts[0].id);
    const returnToSendLink = () => {
        try {
            updateIntegratedStep(sessionId, "SendLinkStep");
            void dispatcher
                .withExtra({
                    nextStep: integratedStep,
                })
                .dispatchBusinessEvent("ButtonClicked_ReSendLink");
        } catch (error) {
            notification.error("Error returning to send link");
            logger.error({ error }, "Error returning to send link");
        }
    };

    return (
        <Flex direction="column" align="center" pl="xl" pr="xl" w="100%">
            <Flex pb="md" pt="lg">
                <img alt={"Tablet with scrolling text"} src={EnrollmentWait} height={150} />
            </Flex>

            <Flex fz="lg" fw={700} aria-label="enrollment-waiting-text">
                Waiting on them to enroll
            </Flex>

            <Flex fz="sm" pt="md" pb="xxl">
                Heads up—this link will expire in 120 minutes.
            </Flex>

            <Tooltip label="The link can only be sent 3 times" position="bottom" disabled={!maximumSmartLinksGenerated}>
                <Button
                    color="dark.5"
                    className={maximumSmartLinksGenerated ? classes.disabledButton : classes.enabledButton}
                    onClick={() => {
                        void returnToSendLink();
                    }}
                    disabled={maximumSmartLinksGenerated}
                    size={"sm"}
                >
                    Re-send link
                </Button>
            </Tooltip>

            <Button variant="subtle" color="white" onClick={open} mt="sm" size={"sm"}>
                <Text td="underline" inherit>
                    Preview Disclosures
                </Text>
            </Button>

            <FTCDisclosureModal
                opened={opened}
                close={close}
                productSku={product.productSku}
                language={language}
                requiresNyDisclosures={product.requiresNyDisclosures ?? false}
            />
        </Flex>
    );
}
